import { useState, useEffect, useRef } from "react";
import api, { errDetail, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ImageOff, Upload } from "lucide-react";

const LogoUpload = ({ value, onChange }) => {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Logo yüklendi");
    } catch (e) { toast.error(errDetail(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden border border-gray-200 bg-gray-50">
        {value ? <img src={`${BACKEND_URL}${value}`} alt="Logo" className="h-full w-full object-contain" /> : <ImageOff size={20} className="text-gray-300" />}
      </div>
      <button type="button" data-testid="settings-logo-upload" onClick={() => ref.current?.click()}
        className="flex items-center gap-2 border border-gray-200 px-4 py-2 text-sm transition-colors duration-200 hover:border-[#D4AF37]">
        <Upload size={15} /> {uploading ? "Yükleniyor..." : "Logo Seç"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
    </div>
  );
};

export default function Settings() {
  const { hasPerm } = useAuth();
  const { refreshBranding } = useBranding();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Ayarlar kaydedildi");
      refreshBranding();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  if (!form) return null;

  return (
    <div data-testid="settings-page">
      <PageHeader title="Ayarlar" subtitle="Firma bilgileri ve sistem ayarları" />
      <div className="max-w-2xl border border-gray-200 bg-white p-6 md:p-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Firma Adı</Label>
            <Input data-testid="settings-company-name" className="rounded-none" value={form.company_name || ""}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Telefon</Label>
            <Input data-testid="settings-phone" className="rounded-none" value={form.phone || ""}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Vergi Numarası</Label>
            <Input data-testid="settings-tax-number" className="rounded-none" value={form.tax_number || ""}
              onChange={(e) => setForm({ ...form, tax_number: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Adres</Label>
            <Textarea data-testid="settings-address" className="rounded-none" value={form.address || ""}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Para Birimi</Label>
            <Input data-testid="settings-currency" className="rounded-none" value={form.currency || "₺"}
              onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </div>
        </div>
        {hasPerm("settings", "edit") && (
          <div className="mt-8 flex justify-end">
            <Button data-testid="settings-save-button" disabled={saving} onClick={save}
              className="rounded-none bg-[#0B1F3A] px-8 text-white hover:bg-[#132c4f]">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 max-w-2xl border border-gray-200 bg-white p-6 md:p-8">
        <h3 className="font-heading text-base font-semibold text-[#0B1F3A]">Marka / Görünüm</h3>
        <p className="mt-1 text-sm text-gray-500">Sistemde görünen isim ve logo — sol altta yer alan "Powered By KMS Media Ajans" yazısı sabittir.</p>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Sistem Adı</Label>
            <Input data-testid="settings-site-name" className="rounded-none" value={form.site_name || ""}
              onChange={(e) => setForm({ ...form, site_name: e.target.value })} />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Logo</Label>
            <LogoUpload value={form.logo_url} onChange={(url) => { setForm({ ...form, logo_url: url }); refreshBranding(); }} />
          </div>
        </div>
        {hasPerm("settings", "edit") && (
          <div className="mt-8 flex justify-end">
            <Button data-testid="settings-branding-save-button" disabled={saving} onClick={save}
              className="rounded-none bg-[#0B1F3A] px-8 text-white hover:bg-[#132c4f]">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6 max-w-2xl border border-gray-200 bg-white p-6 md:p-8">
        <h3 className="font-heading text-base font-semibold text-[#0B1F3A]">Yıl Dönümü Hatırlatma Mesajı</h3>
        <p className="mt-1 text-sm text-gray-500">
          Geçmiş yıllarda bugünün tarihinde "Tamamlandı" durumuna alınmış bir randevu varsa (silinmemiş olması yeterli), bildirim panelinde her yıl bu mesaj gösterilir.
        </p>
        <div className="mt-5">
          <Textarea data-testid="settings-anniversary-template" className="rounded-none" rows={3}
            value={form.anniversary_message_template || ""}
            onChange={(e) => setForm({ ...form, anniversary_message_template: e.target.value })} />
          <p className="mt-2 text-xs text-gray-400">
            Kullanılabilir alanlar: <span className="font-mono">{"{musteri}"}</span>, <span className="font-mono">{"{yil_once}"}</span>,{" "}
            <span className="font-mono">{"{tarih}"}</span>, <span className="font-mono">{"{urunler}"}</span>
          </p>
        </div>
        {hasPerm("settings", "edit") && (
          <div className="mt-8 flex justify-end">
            <Button data-testid="settings-anniversary-save-button" disabled={saving} onClick={save}
              className="rounded-none bg-[#0B1F3A] px-8 text-white hover:bg-[#132c4f]">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
